/** Service-time 86 flags and optional remaining counts. Not part of catalog JSON. */

import { parseMenuChoiceSelection } from './menuOptionSelection'

export type SoldOutChoice = {
  optionId: string
  choice: string
}

export type SoldOutReason = 'manual' | 'depleted'

export type SoldOutDraftState = {
  soldOut: boolean
  choices: SoldOutChoice[]
  /** Infinity off. A number is not required until staff type one. */
  tracking: boolean
  stockRemaining?: number
  soldOutReason?: SoldOutReason
}

export type SoldOutOption = {
  id: string
  type?: string
  choices?: string[]
}

export type SoldOutFlagItem = {
  soldOut?: boolean
  soldOutChoices?: SoldOutChoice[]
  soldOutReason?: SoldOutReason
  /** Present means tracked. Omit = unlimited. */
  stockRemaining?: number
  options?: SoldOutOption[] | string[]
}

export const LOW_STOCK_WARN_AT = 10

export function isSoldOutItem(item: { soldOut?: boolean } | null | undefined): boolean {
  return item?.soldOut === true
}

export function normalizeSoldOutChoices(raw: unknown): SoldOutChoice[] | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined
  const out: SoldOutChoice[] = []
  const seen = new Set<string>()
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue
    const rec = row as { optionId?: unknown; choice?: unknown }
    const optionId = typeof rec.optionId === 'string' ? rec.optionId.trim() : ''
    const choice = typeof rec.choice === 'string' ? rec.choice.trim() : ''
    if (!optionId || !choice) continue
    const key = `${optionId}\0${choice}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push({ optionId, choice })
  }
  return out.length > 0 ? out : undefined
}

export function soldOutChoiceKey(optionId: string, choice: string): string {
  return `${optionId}\0${choice}`
}

export function normalizeStockRemaining(raw: unknown): number | undefined {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return undefined
  return Math.max(0, Math.floor(raw))
}

export function normalizeSoldOutReason(raw: unknown): SoldOutReason | undefined {
  return raw === 'manual' || raw === 'depleted' ? raw : undefined
}

export function isStockTracked(
  item: { stockRemaining?: number } | null | undefined,
): boolean {
  return typeof item?.stockRemaining === 'number'
}

export function isChoiceBearingOption(opt: SoldOutOption | null | undefined): boolean {
  if (!opt) return false
  if (opt.type === 'text') return false
  return Array.isArray(opt.choices) && opt.choices.length > 0
}

export function itemHasChoiceBearingOptions(item: {
  options?: SoldOutOption[] | string[]
}): boolean {
  const opts = item.options
  if (!Array.isArray(opts) || opts.length === 0) return false
  if (typeof opts[0] === 'string') return true
  return (opts as SoldOutOption[]).some(isChoiceBearingOption)
}

export function isSoldOutChoice(
  soldOutChoices: SoldOutChoice[] | undefined,
  optionId: string,
  choice: string,
): boolean {
  return soldOutChoices?.some((row) => row.optionId === optionId && row.choice === choice) === true
}

export function availableChoices(
  option: SoldOutOption,
  soldOutChoices?: SoldOutChoice[],
): string[] {
  return (option.choices ?? []).filter((choice) => !isSoldOutChoice(soldOutChoices, option.id, choice))
}

export function radioGroupFullySoldOut(
  option: SoldOutOption,
  soldOutChoices?: SoldOutChoice[],
): boolean {
  if (option.type !== 'radio' && option.type !== 'frequency') return false
  const choices = option.choices ?? []
  if (choices.length === 0) return false
  return choices.every((choice) => isSoldOutChoice(soldOutChoices, option.id, choice))
}

export function itemBlockedBySoldOutOptions(item: SoldOutFlagItem | null | undefined): boolean {
  if (!item) return false
  if (item.soldOut === true) return true
  const opts = item.options
  if (!Array.isArray(opts) || opts.length === 0 || typeof opts[0] === 'string') return false
  return (opts as SoldOutOption[]).some((opt) => radioGroupFullySoldOut(opt, item.soldOutChoices))
}

/** Whole-item 86, blocked options, or tracked remaining at 0. Missing stock = unlimited. */
export function itemIsOrderable(item: SoldOutFlagItem | null | undefined): boolean {
  if (!item) return false
  if (item.soldOut === true) return false
  if (itemBlockedBySoldOutOptions(item)) return false
  const remaining = item.stockRemaining
  if (remaining === undefined) return true
  return remaining > 0
}

export function soldOutChoiceNamesInSelection(
  item: SoldOutFlagItem,
  optionsSelected: readonly string[],
): string[] {
  const sold = item.soldOutChoices
  if (!sold?.length || optionsSelected.length === 0) return []
  const opts = item.options
  if (!Array.isArray(opts) || opts.length === 0) return []
  if (typeof opts[0] === 'string') {
    const group = new Set(opts as string[])
    return optionsSelected.filter(
      (sel) => group.has(sel) && sold.some((row) => row.choice === sel),
    )
  }
  const names: string[] = []
  for (const opt of opts as SoldOutOption[]) {
    if (!isChoiceBearingOption(opt)) continue
    const group = new Set(opt.choices ?? [])
    for (const sel of optionsSelected) {
      const parsed = parseMenuChoiceSelection(sel)
      if (parsed.optId && parsed.optId !== opt.id) continue
      if (!group.has(parsed.choice)) continue
      if (isSoldOutChoice(sold, opt.id, parsed.choice)) names.push(parsed.choice)
    }
  }
  return names
}

export function soldOutRowCount(item: SoldOutFlagItem | null | undefined): number {
  if (!item) return 0
  return (item.soldOut === true ? 1 : 0) + (item.soldOutChoices?.length ?? 0)
}

export function draftFromSoldOutItem(item: SoldOutFlagItem): SoldOutDraftState | undefined {
  const choices = normalizeSoldOutChoices(item.soldOutChoices) ?? []
  const remaining = normalizeStockRemaining(item.stockRemaining)
  const tracking = remaining !== undefined
  const soldOut = item.soldOut === true
  if (!soldOut && choices.length === 0 && !tracking) return undefined
  return {
    soldOut,
    choices,
    tracking,
    ...(remaining !== undefined ? { stockRemaining: remaining } : {}),
    ...(soldOut ? { soldOutReason: normalizeSoldOutReason(item.soldOutReason) ?? 'manual' } : {}),
  }
}

export function draftIsInteresting(state: SoldOutDraftState): boolean {
  return (
    state.soldOut ||
    state.choices.length > 0 ||
    state.tracking
  )
}

/** Apply × / remaining-0 rules for a modal save. Empty tracking (no number) stays unlimited. */
export function persistSoldOutDraft(state: SoldOutDraftState): SoldOutFlagItem {
  const trackedRemaining =
    state.tracking ? normalizeStockRemaining(state.stockRemaining) : undefined
  let soldOut = state.soldOut
  let reason = normalizeSoldOutReason(state.soldOutReason)
  if (state.soldOut) {
    soldOut = true
    reason = 'manual'
  } else if (trackedRemaining === 0) {
    soldOut = true
    reason = 'depleted'
  } else {
    soldOut = false
    reason = undefined
  }
  return withNormalizedSoldOut({
    soldOut,
    soldOutChoices: state.choices,
    stockRemaining: trackedRemaining,
    soldOutReason: reason,
  })
}

/** Keep sparse 86 + stock fields only. */
export function withNormalizedSoldOut<T extends SoldOutFlagItem>(item: T): T {
  const { soldOut, soldOutChoices, soldOutReason, stockRemaining, ...rest } = item
  const choices = normalizeSoldOutChoices(soldOutChoices)
  const remaining = normalizeStockRemaining(stockRemaining)
  const sold = soldOut === true
  const reason = sold ? normalizeSoldOutReason(soldOutReason) : undefined
  return {
    ...(rest as T),
    ...(sold ? { soldOut: true } : {}),
    ...(choices ? { soldOutChoices: choices } : {}),
    ...(sold && reason ? { soldOutReason: reason } : {}),
    ...(remaining !== undefined ? { stockRemaining: remaining } : {}),
  }
}

/**
 * Catalog writes often omit 86/stock fields. Preserve previous unless incoming
 * sets them explicitly (`soldOut` true/false, `soldOutChoices` array,
 * `stockRemaining` number or null to clear).
 */
export function mergeSoldOutFromPrevious<T extends SoldOutFlagItem>(
  next: T,
  previous: T | undefined,
): T {
  const {
    soldOut: incoming,
    soldOutChoices: incomingChoices,
    soldOutReason: incomingReason,
    stockRemaining: incomingRemaining,
    ...rest
  } = next
  const soldOut =
    incoming === true ? true : incoming === false ? false : previous?.soldOut === true
  const choices =
    incomingChoices === undefined
      ? previous?.soldOutChoices
      : normalizeSoldOutChoices(incomingChoices)
  const remaining =
    incomingRemaining === undefined
      ? previous?.stockRemaining
      : normalizeStockRemaining(incomingRemaining)
  const reason = soldOut
    ? incomingReason !== undefined
      ? normalizeSoldOutReason(incomingReason)
      : previous?.soldOutReason
    : undefined
  return withNormalizedSoldOut({
    ...(rest as T),
    ...(soldOut ? { soldOut: true } : {}),
    ...(choices ? { soldOutChoices: choices } : {}),
    ...(remaining !== undefined ? { stockRemaining: remaining } : {}),
    ...(reason ? { soldOutReason: reason } : {}),
  })
}

export function soldOutStatesEqual(a: SoldOutFlagItem, b: SoldOutFlagItem): boolean {
  if ((a.soldOut === true) !== (b.soldOut === true)) return false
  if ((a.soldOutReason ?? undefined) !== (b.soldOutReason ?? undefined)) return false
  if ((a.stockRemaining ?? undefined) !== (b.stockRemaining ?? undefined)) return false
  const left = normalizeSoldOutChoices(a.soldOutChoices) ?? []
  const right = normalizeSoldOutChoices(b.soldOutChoices) ?? []
  if (left.length !== right.length) return false
  const rightKeys = new Set(right.map((row) => soldOutChoiceKey(row.optionId, row.choice)))
  return left.every((row) => rightKeys.has(soldOutChoiceKey(row.optionId, row.choice)))
}

export type StockSendWarningKind = 'over' | 'low'

export type StockSendWarning = {
  foodId: string
  name: string
  remaining: number
  quantity: number
  kind: StockSendWarningKind
}

/** Ticket lines that are over remaining, or tracked below LOW_STOCK_WARN_AT. Never blocks send. */
export function stockSendWarnings(
  lines: ReadonlyArray<{
    foodId?: string
    quantity?: number
    cancelledAt?: string | null
  }>,
  menuItems: ReadonlyArray<{
    id?: string
    name?: string
    stockRemaining?: number
  }>,
): StockSendWarning[] {
  const byId = new Map<string, { name?: string; stockRemaining?: number }>()
  for (const item of menuItems) {
    const id = typeof item.id === 'string' ? item.id.trim() : ''
    if (id) byId.set(id, item)
  }
  const out: StockSendWarning[] = []
  for (const [foodId, quantity] of consumeQtyByFoodId(lines)) {
    const item = byId.get(foodId)
    const remaining = normalizeStockRemaining(item?.stockRemaining)
    if (remaining === undefined || remaining === 0) continue
    const kind: StockSendWarningKind | null =
      quantity > remaining ? 'over' : remaining < LOW_STOCK_WARN_AT ? 'low' : null
    if (!kind) continue
    out.push({
      foodId,
      name: item?.name?.trim() || foodId,
      remaining,
      quantity,
      kind,
    })
  }
  return out
}

export function formatStockSendWarning(row: StockSendWarning): string {
  if (row.kind === 'over') {
    return `${row.name} — ${row.remaining} left, this order has ${row.quantity}`
  }
  return `${row.name} — ${row.remaining} left`
}

export function consumeQtyByFoodId(
  lines: ReadonlyArray<{
    foodId?: string
    quantity?: number
    cancelledAt?: string | null
  }>,
): Map<string, number> {
  const map = new Map<string, number>()
  for (const line of lines) {
    if (line.cancelledAt) continue
    const id = typeof line.foodId === 'string' ? line.foodId.trim() : ''
    if (!id) continue
    const q =
      typeof line.quantity === 'number' && Number.isFinite(line.quantity)
        ? Math.max(0, Math.floor(line.quantity))
        : 0
    if (q <= 0) continue
    map.set(id, (map.get(id) ?? 0) + q)
  }
  return map
}

/** Positive = consume from remaining. Negative = restock. */
export function stockConsumeDeltas(
  prev: ReadonlyMap<string, number>,
  next: ReadonlyMap<string, number>,
): Map<string, number> {
  const ids = new Set([...prev.keys(), ...next.keys()])
  const out = new Map<string, number>()
  for (const id of ids) {
    const delta = (next.get(id) ?? 0) - (prev.get(id) ?? 0)
    if (delta !== 0) out.set(id, delta)
  }
  return out
}

export function applyStockConsumeToItem<T extends SoldOutFlagItem>(
  item: T,
  consume: number,
): T {
  const remaining = normalizeStockRemaining(item.stockRemaining)
  if (remaining === undefined || consume === 0) return item
  const nextRemaining = Math.max(0, remaining - consume)
  let soldOut = item.soldOut === true
  let reason = normalizeSoldOutReason(item.soldOutReason)
  if (consume > 0 && nextRemaining === 0) {
    soldOut = true
    if (reason !== 'manual') reason = 'depleted'
  }
  if (consume < 0 && nextRemaining > 0 && reason === 'depleted') {
    soldOut = false
    reason = undefined
  }
  return withNormalizedSoldOut({
    ...item,
    stockRemaining: nextRemaining,
    soldOut,
    soldOutReason: reason,
  })
}
